/**
 * Task #170 — console.log verbosity audit.
 *
 * 956 raw `console.log` call sites were found across this app's source at
 * the time of this audit — nearly all of them genuinely useful diagnostics
 * while developing (session/project wiring, auth flows, preview/build
 * pipeline state, git sync), just never gated behind anything, so every one
 * fires in a real production build too, permanently flooding any end user's
 * browser console.
 *
 * `devLog`/`devWarn` are drop-in replacements that only print when
 * `import.meta.env.DEV` is true (Vite's own build-time flag — false in a
 * real `vite build`, true under `vite dev`) OR an explicit opt-in escape
 * hatch is set (`localStorage.setItem('mia_flow_debug_logs', 'true')`),
 * for debugging a deployed build without needing a local dev server. This
 * keeps every one of those diagnostics exactly as useful for local
 * development as a raw `console.log` always was, while a real production
 * build stays silent by default.
 *
 * `console.error`/`console.warn` call sites are DELIBERATELY untouched by
 * this audit — those are far more likely to matter to whoever is looking at
 * a real user's console (an actual failure, not routine trace noise), and
 * conflating "verbose trace" with "something went wrong" would be exactly
 * the kind of over-broad fix that trades one problem for a worse one.
 */

function debugLogsEnabled() {
  if (import.meta.env.DEV) {
    return true;
  }
  try {
    return localStorage.getItem('mia_flow_debug_logs') === 'true';
  } catch {
    // localStorage can throw in some contexts (private browsing, disabled
    // storage) — treat that the same as "not enabled" rather than crash.
    return false;
  }
}

export function devLog(...args) {
  if (debugLogsEnabled()) {
    // eslint-disable-next-line no-console
    console.log(...args);
  }
}

export function devWarn(...args) {
  if (debugLogsEnabled()) {
    // eslint-disable-next-line no-console
    console.warn(...args);
  }
}
